import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiCpu, FiLoader } from 'react-icons/fi';
import { configuracaoIAAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';
import { ConfiguracaoIACard } from '../components/configuracaoIA';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0;
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${theme.spacing[6]};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.text.muted};
`;

const LoadingSpinner = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  animation: spin 1s linear infinite;
  margin-bottom: ${theme.spacing[4]};

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: ${theme.fontSizes.base};
  margin: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.text.muted};
  margin-bottom: ${theme.spacing[4]};
`;

const EmptyTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const EmptyText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.error[500]};
  margin-bottom: ${theme.spacing[4]};
`;

const ErrorTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const ErrorText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ErrorAction = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.error[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.error[600]};
  }
`;

const ConfiguracaoIA = () => {
  const [parametros, setParametros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carregar parâmetros
  const carregarParametros = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await configuracaoIAAPI.listar();
      
      if (response.data.success) {
        setParametros(response.data.data);
      } else {
        setError('Erro ao carregar configurações de IA');
      }
    } catch (err) {
      console.error('Erro ao carregar configurações de IA:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados na inicialização
  useEffect(() => {
    carregarParametros();
  }, []);

  // Renderizar conteúdo baseado no estado
  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <LoadingSpinner>
            <FiLoader />
          </LoadingSpinner>
          <LoadingText>Carregando configurações de IA...</LoadingText>
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <ErrorState>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorTitle>Erro ao carregar configurações</ErrorTitle>
          <ErrorText>{error}</ErrorText>
          <ErrorAction onClick={carregarParametros}>
            Tentar novamente
          </ErrorAction>
        </ErrorState>
      );
    }

    if (parametros.length === 0) {
      return (
        <EmptyState>
          <EmptyIcon>
            <FiCpu />
          </EmptyIcon>
          <EmptyTitle>Nenhuma configuração disponível</EmptyTitle>
          <EmptyText>
            Não foram encontradas configurações de IA para seus clientes.
            Entre em contato com o administrador para habilitar essa funcionalidade.
          </EmptyText>
        </EmptyState>
      );
    }

    return (
      <CardsGrid>
        {parametros.map((parametro) => (
          <ConfiguracaoIACard 
            key={parametro.uuid || parametro.id}
            parametro={parametro}
          />
        ))}
      </CardsGrid>
    );
  };

  return (
    <Layout title="Configuração de IA">
      <Container>
        <Header>
          <Title>Configuração de IA</Title>
          <Subtitle>
            Personalize como a inteligência artificial responde seus leads no WhatsApp
          </Subtitle>
        </Header>

        <Content>
          {renderContent()}
        </Content>
      </Container>
    </Layout>
  );
};

export default ConfiguracaoIA;
